/* One-tap referral share hero.
 *
 * Behavior by device:
 *  • Mobile w/ Web Share API → native share sheet (pulls contacts: iMessage,
 *    WhatsApp, Messages, etc.) — single tap, zero friction
 *  • Mobile without Web Share → opens native SMS app with pre-filled body
 *  • Desktop → opens iMessage/Messages app (macOS) OR copies link + opens
 *    SMS protocol handler if registered, OR falls back to clipboard copy
 *
 * Hero CTA is intentionally dominant — the entire conversion math depends
 * on referrers actually sharing, so this needs to feel like the #1 thing
 * to do on this page.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Send, Copy, Check, Smartphone, Loader2 } from "lucide-react";

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

function hasWebShare() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export default function ShareViaTextHero({ shareUrl, shareMsg }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const mobile = isMobile();
  const webShare = hasWebShare();

  const onNativeShare = async () => {
    if (!shareUrl) return;
    setSharing(true);
    try {
      await navigator.share({
        title: "iSpeedToClose — free month inside",
        text: shareMsg,
        url: shareUrl,
      });
      // Successful share doesn't tell us which app was used, but it
      // definitely went through. Toast for confirmation.
      toast.success("Shared!");
    } catch (e) {
      // AbortError = user cancelled the share sheet. Don't show an error.
      if (e?.name !== "AbortError") {
        toast.error("Couldn't open share sheet — try the copy button below.");
      }
    } finally {
      setSharing(false);
    }
  };

  const onSmsClick = () => {
    // iOS uses ?body=, Android uses &body= (but ? also works as the first
    // parameter). Use `&body=` after a leading `?` separator so both work.
    const sep = mobile ? "?" : "?";  // both platforms accept ?body=
    const href = `sms:${sep}body=${encodeURIComponent(shareMsg)}`;
    window.location.href = href;
  };

  const onCopy = () => {
    navigator.clipboard.writeText(shareMsg);
    setCopied(true);
    toast.success("Message copied — paste it anywhere.");
    setTimeout(() => setCopied(false), 2200);
  };

  if (!shareUrl) return null;

  return (
    <div
      className="mb-5 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-5 py-5"
      data-testid="referrals-share-hero"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-white border border-indigo-200 flex items-center justify-center shrink-0">
          <Smartphone size={18} className="text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold text-lg leading-tight">
            Text a wholesaler buddy — both of you get a free month
          </div>
          <div className="text-xs text-[var(--ink-2)] mt-1 leading-relaxed">
            Pre-filled message, one tap. {mobile && webShare
              ? "Opens your contacts so you can pick anyone in your phone."
              : mobile
              ? "Opens your Messages app with the link ready to send."
              : "Opens your default messaging app — or copy the message and text it from your phone."}
          </div>
        </div>
      </div>

      {/* Preview of the message they'll send */}
      <div
        className="bg-white border border-indigo-100 rounded-lg p-3 mb-4 text-xs text-[var(--ink-2)] leading-relaxed font-mono"
        data-testid="share-hero-preview"
      >
        {shareMsg}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {webShare && mobile ? (
          <button
            type="button"
            onClick={onNativeShare}
            disabled={sharing}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition disabled:bg-emerald-300 shadow-sm"
            data-testid="share-hero-native-btn"
          >
            {sharing
              ? <><Loader2 size={16} className="animate-spin" /> Opening share sheet…</>
              : <><Send size={16} /> Share with a friend</>}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSmsClick}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition shadow-sm"
            data-testid="share-hero-sms-btn"
          >
            <Send size={16} />
            {mobile ? "Text it now" : "Open Messages"}
          </button>
        )}
        <button
          type="button"
          onClick={onCopy}
          className="sm:w-[180px] inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 font-bold transition"
          data-testid="share-hero-copy-btn"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Copied!" : "Copy message"}
        </button>
      </div>

      <div className="text-[11px] text-[var(--ink-3)] mt-3 text-center">
        💡 Tip: text-sharing converts <strong>3-4× higher</strong> than copy-paste.
        Pro wholesalers run this play on Monday mornings.
      </div>
    </div>
  );
}
